import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

const MIN_MARQUEE_COPIES = 2;
const MIN_MARQUEE_SPEED_SECONDS = 10;

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 * @property {HTMLElement[]} marqueeItems - The marquee items collection.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content', 'marqueeItems'];

  #initialized = false;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {number | null} */
  #marqueeWidth = null;

  connectedCallback() {
    super.connectedCallback();

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);

    this.#resizeObserver = new ResizeObserver(() => {
      if (this.#initialized) {
        this.#handleResize();
      } else if (this.clientWidth > 0) {
        this.#initialize();
      }
    });
    this.#resizeObserver.observe(this);

    if (this.clientWidth > 0) {
      this.#initialize();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
  }

  /**
   * @type {{ cancel: () => void, current: number } | null}
   */
  #animation = null;

  #slowDown = debounce(() => {
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];

    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => {
        this.#animation = null;
      },
    });
  }

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;

    return content !== lastChild ? lastChild : null;
  }

  async #initialize() {
    if (this.#initialized) return;

    const { marqueeItems } = this.refs;
    if (marqueeItems.length === 0) return;

    const { numberOfCopies } = await this.#queryNumberOfCopies();
    const speed = this.#calculateSpeed(numberOfCopies);

    this.#setRepeatedItems(numberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(speed);
    this.#initialized = true;
  }

  /**
   * @param {number} value
   */
  #setSpeed(value) {
    this.style.setProperty('--marquee-speed', `${value}s`);
    this.#restartAnimation();
  }

  async #queryNumberOfCopies() {
    const { marqueeItems } = this.refs;

    await new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });

    if (!marqueeItems[0]) {
      return { numberOfCopies: MIN_MARQUEE_COPIES, isHorizontalResize: true };
    }

    const marqueeWidth = this.clientWidth;
    const marqueeItemsWidth = marqueeItems[0].getBoundingClientRect().width;

    const isHorizontalResize = this.#marqueeWidth !== marqueeWidth;
    this.#marqueeWidth = marqueeWidth;

    let numberOfCopies = MIN_MARQUEE_COPIES;
    if (marqueeItemsWidth > 0 && marqueeWidth > 0) {
      numberOfCopies = Math.max(MIN_MARQUEE_COPIES, Math.ceil(marqueeWidth / marqueeItemsWidth));
    }

    return { numberOfCopies, isHorizontalResize };
  }

  /**
   * @param {number} numberOfCopies
   */
  #calculateSpeed(numberOfCopies) {
    const speedFactor = Number(this.getAttribute('data-speed-factor')) || 25;
    const speed = Math.sqrt(Math.max(1, numberOfCopies)) * speedFactor;

    return Math.max(MIN_MARQUEE_SPEED_SECONDS, speed);
  }

  #handleResize = debounce(async () => {
    if (!this.#initialized) return;

    const { numberOfCopies: newNumberOfCopies, isHorizontalResize } = await this.#queryNumberOfCopies();

    if (!isHorizontalResize) return;

    this.#setRepeatedItems(newNumberOfCopies);
    this.#duplicateContent();
    this.#setSpeed(this.#calculateSpeed(newNumberOfCopies));
  }, 250);

  #restartAnimation() {
    const animations = this.refs.wrapper.getAnimations();

    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();

    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));

    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');

    this.refs.wrapper.appendChild(clone);
  }

  /**
   * @param {number} totalCopies
   */
  #setRepeatedItems(totalCopies) {
    const { content, marqueeItems } = this.refs;

    if (!marqueeItems[0]) return;

    const targetCopies = Math.max(MIN_MARQUEE_COPIES, totalCopies);
    const currentCopies = content.children.length;

    if (targetCopies > currentCopies) {
      for (let i = 0; i < targetCopies - currentCopies; i++) {
        content.appendChild(marqueeItems[0].cloneNode(true));
      }
      return;
    }

    if (targetCopies < currentCopies) {
      for (let i = 0; i < currentCopies - targetCopies; i++) {
        content.lastElementChild?.remove();
      }
    }
  }
}

// Define the animateValue function
/**
 * Animate a numeric property smoothly.
 * @param {Object} params - The parameters for the animation.
 * @param {number} params.from - The starting value.
 * @param {number} params.to - The ending value.
 * @param {number} params.duration - The duration of the animation in milliseconds.
 * @param {function(number): void} params.onUpdate - The function to call on each update.
 * @param {function(number): number} [params.easing] - The easing function.
 * @param {function(): void} [params.onComplete] - The function to call when the animation completes.
 */
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  /**
   * @param {number} currentTime - The current time in milliseconds.
   */
  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}
